import { DeviceStatus } from '../../../constants/DeviceStatus';

export interface FeedConfig {
  storage: any;
  materialType: string;
  materialName: string;
  binId: number;
  plantId: number;
  target: number;
  cutoff: number;
  inventory: number;
  idlingDuration?: number;
  batchOrder?: number;
  prevFeedMetrics?: FeedMetrics;
  prevDaySettlingWeight?: number;
}

export interface FeedStatus {
  weight?: number;
  binStatus?: string;
  aeratorPNoStatus?: number;
  binGatePNoStatus?: number;
  dustCollectorPNoStatus?: number;
  scalePNoStatus?: number;
  vibratorPNoStatus?: number;
  [key: string]: any;
}

export interface FeedDataInput extends FeedStatus {}

export interface FeedDataType extends FeedStatus {
  timestamp: number;
}

export interface FeedMetrics {
  timestamp: number;
  batchOrder: number;
  feedEndTime: number;
  feedDuration: number;
  feedWeightChange: number;
  target: number;
  cutoff: number;
  settlingWeight: number;
  flowMean: number;
  flowMax: number;
  flowStd: number;
  flowMeanLast5s: number;
  flowMeanLast4s: number;
  flowMeanLast3s: number;
  flowMeanLast2s: number;
  flowMeanLast1s: number;
  flowMeanLastDp: number;
  flowAccMean: number;
  flowAccMax: number;
  flowAccMeanLast5s: number;
  flowAccMeanLast4s: number;
  flowAccMeanLast3s: number;
  flowAccMeanLast2s: number;
  flowAccMeanLast1s: number;
  flowAccMeanLastDp: number;
  flowSkew: number;
  idlingDuration: number;
}

const prevFeedProperties = [
  'target',
  'cutoff',
  'feedDuration',
  'feedWeightChange',
  'settlingWeight',
  'flowMean',
  'flowMax',
  'flowStd',
  'flowMeanLast5s',
  'flowMeanLast4s',
  'flowMeanLast3s',
  'flowMeanLast2s',
  'flowMeanLast1s',
  'flowMeanLastDp',
  'flowAccMean',
  'flowAccMax',
  'flowAccMeanLast5s',
  'flowAccMeanLast4s',
  'flowAccMeanLast3s',
  'flowAccMeanLast2s',
  'flowAccMeanLast1s',
  'flowAccMeanLastDp',
  'flowSkew',
  'idlingDuration',
] as const;

export class FeedData implements FeedDataType {
  timestamp: number;
  weight?: number;
  binStatus?: string;
  aeratorPNoStatus?: number;
  binGatePNoStatus?: number;
  dustCollectorPNoStatus?: number;
  scalePNoStatus?: number;
  vibratorPNoStatus?: number;
  [key: string]: any;

  constructor(data: FeedDataInput) {
    this.timestamp = Date.now();
    this.weight = data?.weight;
    this.binStatus = data?.binStatus;
    this.aeratorPNoStatus = data?.aeratorPNoStatus;
    this.binGatePNoStatus = data?.binGatePNoStatus;
    this.dustCollectorPNoStatus = data?.dustCollectorPNoStatus;
    this.scalePNoStatus = data?.scalePNoStatus;
    this.vibratorPNoStatus = data?.vibratorPNoStatus;
  }
}

export interface ProcessedFeedData {
  timestamp: number;
  weight: number;
  deltaTime: number;
  elapsedTime: number;
  deltaWeight: number;
  flowRate: number;
  flowAcc?: number;
}

export interface FlowStats {
  mean: number;
  max: number;
  std: number;
}

export interface FlowMetrics {
  totalDuration: number;
  totalWeightChange: number;
  flowSkew: number;
  lastWeight: number;
}

export class FeedTracker {
  storage: any;
  feedMetricsKey: string;
  feedDataKey : string;
  feedMetricsSettlingWeightKey: string;
  materialType: string;
  materialName: string;
  binId: number;
  plantId: number;
  target: number;
  cutoff: number;
  inventory: number;
  idlingDuration?: number;
  batchOrder?: number;
  feedStartTime: number;
  feedData: FeedDataInput[];
  prevFeedMetrics?: FeedMetrics;
  prevDaySettlingWeight?: number;
  feedEndTime?: number;
  feedWeight?: number;
  feedTime?: number;
  settlingEndTime?: number;
  settlingWeight?: number;
  settlingTime?: number;

  constructor(config: FeedConfig) {
    this.storage = config.storage || window.localStorage;
    this.binId = config.binId;
    this.plantId = config.plantId;
    this.materialType = config.materialType;
    this.materialName = config.materialName;
    this.target = config.target;
    this.cutoff = config.cutoff;
    this.inventory = config.inventory;
    this.feedStartTime = Date.now();
    this.feedData = [];
    this.feedMetricsKey = `feed-metrics-${config.binId}`;
    this.feedDataKey = `feed-data-${config.binId}`;
    this.feedMetricsSettlingWeightKey = `feed-metrics-settling-weight-${config.binId}`;

    this.init();
  }

  init() {
    this.prevFeedMetrics = this.storage.getItem(this.feedMetricsKey);

    if (this.prevFeedMetrics?.timestamp) {
      const d = new Date(this.prevFeedMetrics.timestamp);
      const now = new Date();
      if (d.getDate() !== now.getDate() || d.getMonth() !== now.getMonth()) {
        this.prevFeedMetrics.batchOrder = 0;
      }

      this.batchOrder = (this.prevFeedMetrics.batchOrder || 0) + 1;
      this.idlingDuration = (Date.now() - this.prevFeedMetrics?.feedEndTime) / 1000;
    }

    let feedMetricsSettlingWeight = this.storage.getItem(this.feedMetricsSettlingWeightKey) || [];

    this.prevDaySettlingWeight = getAverage(feedMetricsSettlingWeight
      .filter((v: { timestamp: number; }) => new Date(v.timestamp).getDate() === new Date().getDate() - 1)
      .map((v: { weight: number; }) => v.weight),
    );
  }

  addData(data: FeedDataInput): void {
    const lastFeedData = this.feedData.at(-1) as FeedData;
    if (
      lastFeedData?.binStatus === DeviceStatus.FEED
      && data.binStatus !== DeviceStatus.FEED
    ) {
      this.feedEndTime = Date.now();
    } else if (
      lastFeedData?.binStatus === DeviceStatus.SETTLING
      && data.binStatus !== DeviceStatus.SETTLING
      && this.feedEndTime
      && !this.settlingEndTime
    ) {
      this.settlingEndTime = Date.now();
    }
    this.feedData.push(new FeedData(data));
    this.calculateSettlingWeight();
    this.calculateFeedWeight();
  }

  calculateSettlingWeight(): void {
    if (this.feedEndTime && this.settlingEndTime && !this.settlingTime) {
      // @ts-ignore
      const start = this.feedData.find(v => v.timestamp >= this.feedEndTime);
      // @ts-ignore
      const end = this.feedData.find(v => v.timestamp >= this.settlingEndTime);
      // @ts-ignore
      this.settlingWeight = start && end ? end?.weight - start?.weight : 0;
      this.settlingTime = start && end ? (end.timestamp - start?.timestamp) / 1000 : 0;
    }
  }

  calculateFeedWeight(): void {
    if (this.feedStartTime && this.feedEndTime && !this.feedTime) {
      // @ts-ignore
      const start = this.feedData.find(v => v.timestamp >= this.feedStartTime);
      // @ts-ignore
      const end = this.feedData.find(v => v.timestamp >= this.feedEndTime);
      // @ts-ignore
      this.feedWeight = start && end ? end?.weight - start?.weight : 0;
      this.feedTime = start && end ? (end.timestamp - start?.timestamp) / 1000 : 0;
    }
  }

  finish() {
    // @ts-ignore
    this.feedData = this.feedData.filter(v => v.timestamp <= this.feedEndTime);
    const feedMetrics = this.getCalculatedFeedData();
    this.storage.setItem(this.feedMetricsKey, feedMetrics);
    this.storage.setItem(this.feedDataKey, this.feedData);

    if (this.settlingEndTime) {
      let feedMetricsSettlingWeight = this.storage.getItem(this.feedMetricsSettlingWeightKey) || [];
      const now = Date.now();
      const twoDaysInMs = 2 * 24 * 60 * 60 * 1000;
      // @ts-ignore
      feedMetricsSettlingWeight = feedMetricsSettlingWeight.filter(v => now - v.timestamp <= twoDaysInMs);
      feedMetricsSettlingWeight.push({
        timestamp: this.settlingEndTime,
        weight: this.settlingWeight,
      });
      this.storage.setItem(this.feedMetricsSettlingWeightKey, feedMetricsSettlingWeight);
    }
  }

  getCalculatedFeedData() {
    const date = new Date(this.feedStartTime);
    const processedFeedData = processFeedData(this.feedData);
    const flowRateData = getFlowRateStats(processedFeedData);
    const computedFlowAcceleration = computeFlowAcceleration(processedFeedData);
    const flowRateAccData = computeFlowAccStats(computedFlowAcceleration);
    const flowSkewMetric = computeFlowMetrics(processedFeedData);
    const activePercentageForAerator = getActivePercentage(this.feedData.map(v => v.aeratorPNoStatus ?? 0));
    const nonActivePercentageForAerator = 100 - activePercentageForAerator;
    const activePercentageForBin = getActivePercentage(this.feedData.map(v => v.binGatePNoStatus ?? 0));
    const nonActivePercentageForBin = 100 - activePercentageForBin;
    const activePercentageForVibrator = getActivePercentage(this.feedData.map(v => v.vibratorPNoStatus ?? 0));
    const nonActivePercentageForVibrator = 100 - activePercentageForVibrator;
    const activePercentageForDustCollector = getActivePercentage(this.feedData.map(v => v.dustCollectorPNoStatus ?? 0));
    const nonActivePercentageForDustCollector = 100 - activePercentageForDustCollector;
    const activePercentageForScale = getActivePercentage(this.feedData.map(v => v.scalePNoStatus ?? 0));
    const nonActivePercentageForScale = 100 - activePercentageForScale;
    const lastFeedData = this.feedData.at(-1) as FeedData;
    const calculatedTime = calcActivityTime(this.feedData);
    const prevMetrics = this.prevFeedMetrics ? getPrevMetrics(this.prevFeedMetrics) : {};

    return {
      timestamp: lastFeedData?.timestamp,
      materialName: this.materialName,
      materialType: this.materialType,
      binid: this.binId, // this is correct binid with lowercase
      plantid: this.plantId, // this is correct plantid with lowercase
      binStatus: lastFeedData?.binStatus,
      target: this.target,
      cutoff: this.cutoff,
      inventory: this.inventory,
      batchOrder: this.batchOrder,
      hour: date.getHours(),
      dayOfWeek: date.getDay(),
      month: date.getMonth() + 1, // 0 is January
      feedStartTime: this.feedStartTime,
      feedEndTime: this.feedEndTime,
      feedDuration: this.feedTime,
      totalDuration: flowSkewMetric.totalDuration,
      feedWeight: this.feedWeight,
      feedWeightChange: flowSkewMetric.lastWeight,
      flowMean: flowRateData.mean,
      flowMax: flowRateData.max,
      flowStd: flowRateData.std,
      flowMeanLast5s: meanFlowRateLastByTime(processedFeedData, 5),
      flowMeanLast4s: meanFlowRateLastByTime(processedFeedData, 4),
      flowMeanLast3s: meanFlowRateLastByTime(processedFeedData, 3),
      flowMeanLast2s: meanFlowRateLastByTime(processedFeedData, 2),
      flowMeanLast1s: meanFlowRateLastByTime(processedFeedData, 1),
      flowMeanLastDp: meanFlowRateLastByTime(processedFeedData, 1, false, true),
      flowAccMean: flowRateAccData.mean,
      flowAccMax: flowRateAccData.max,
      flowAccStd: flowRateAccData.std,
      flowAccMeanLast5s: meanFlowRateLastByTime(computedFlowAcceleration, 5, true),
      flowAccMeanLast4s: meanFlowRateLastByTime(computedFlowAcceleration, 4, true),
      flowAccMeanLast3s: meanFlowRateLastByTime(computedFlowAcceleration, 3, true),
      flowAccMeanLast2s: meanFlowRateLastByTime(computedFlowAcceleration, 2, true),
      flowAccMeanLast1s: meanFlowRateLastByTime(computedFlowAcceleration, 1, true),
      flowAccMeanLastDp: meanFlowRateLastByTime(computedFlowAcceleration, 1, true, true),
      flowSkew: flowSkewMetric.flowSkew,
      activatedPnoBin: calculatedTime.binGatePNoStatus?.activated,
      activatedPnoScale: calculatedTime.scalePNoStatus?.activated,
      activatedPnoDust: calculatedTime.dustCollectorPNoStatus?.activated,
      activatedPnoAer: calculatedTime.aeratorPNoStatus?.activated,
      activatedPnoVib: calculatedTime.vibratorPNoStatus?.activated,
      percentageActiveTimePnoAer: activePercentageForAerator,
      percentageNonActiveTimePnoAer: nonActivePercentageForAerator,
      percentageActiveTimePnoBin: activePercentageForBin,
      percentageNonActiveTimePnoBin: nonActivePercentageForBin,
      percentageActiveTimePnoVib: activePercentageForVibrator,
      percentageNonActiveTimePnoVib: nonActivePercentageForVibrator,
      percentageActiveTimePnoDust: activePercentageForDustCollector,
      percentageNonActiveTimePnoDust: nonActivePercentageForDustCollector,
      percentageActiveTimePnoScale: activePercentageForScale,
      percentageNonActiveTimePnoScale: nonActivePercentageForScale,
      totalActiveTimePnoBin: calculatedTime.binGatePNoStatus?.totalActiveTime,
      totalActiveTimePnoAer: calculatedTime.aeratorPNoStatus?.totalActiveTime,
      totalActiveTimePnoVib: calculatedTime.vibratorPNoStatus?.totalActiveTime,
      totalActiveTimePnoDust: calculatedTime.dustCollectorPNoStatus?.totalActiveTime,
      totalActiveTimePnoScale: calculatedTime.scalePNoStatus?.totalActiveTime,
      totalNonActiveTimePnoBin: calculatedTime.binGatePNoStatus?.totalNonActiveTime,
      totalNonActiveTimePnoAer: calculatedTime.aeratorPNoStatus?.totalNonActiveTime,
      totalNonActiveTimePnoVib: calculatedTime.vibratorPNoStatus?.totalNonActiveTime,
      totalNonActiveTimePnoDust: calculatedTime.dustCollectorPNoStatus?.totalNonActiveTime,
      totalNonActiveTimePnoScale: calculatedTime.scalePNoStatus?.totalNonActiveTime,
      averageActiveTimePnoBin: calculatedTime.binGatePNoStatus?.avgActiveTime,
      averageActiveTimePnoAer: calculatedTime.aeratorPNoStatus?.avgActiveTime,
      averageActiveTimePnoVib: calculatedTime.vibratorPNoStatus?.avgActiveTime,
      averageActiveTimePnoDust: calculatedTime.dustCollectorPNoStatus?.avgActiveTime,
      averageActiveTimePnoScale: calculatedTime.scalePNoStatus?.avgActiveTime,
      averageNonActiveTimePnoBin: calculatedTime.binGatePNoStatus?.avgNonActiveTime,
      averageNonActiveTimePnoAer:calculatedTime.aeratorPNoStatus?.avgNonActiveTime,
      averageNonActiveTimePnoVib: calculatedTime.vibratorPNoStatus?.avgNonActiveTime,
      averageNonActiveTimePnoDust: calculatedTime.dustCollectorPNoStatus?.avgNonActiveTime,
      averageNonActiveTimePnoScale: calculatedTime.scalePNoStatus?.avgNonActiveTime,
      activityChangeFreqPnoBin: calculatedTime.binGatePNoStatus?.activityChangeFreq,
      activityChangeFreqPnoAer: calculatedTime.aeratorPNoStatus?.activityChangeFreq,
      activityChangeFreqPnoVib: calculatedTime.vibratorPNoStatus?.activityChangeFreq,
      activityChangeFreqPnoDust: calculatedTime.dustCollectorPNoStatus?.activityChangeFreq,
      activityChangeFreqPnoScale: calculatedTime.scalePNoStatus?.activityChangeFreq,
      settlingWeight: this.settlingWeight,
      settlingTime: this.settlingTime,
      idlingDuration: this.idlingDuration,
      ...prevMetrics,
      prevDaySettlingWeight: this.prevDaySettlingWeight,
    };
  }
}

/**
 * Process feed data with millisecond timestamps. Calculates deltaTime, elapsedTime, deltaWeight, and flowRate.
 * @param feedData
 * @returns Processed data.
 */
export const processFeedData = (feedData: FeedDataInput[]): ProcessedFeedData[] => {
  if (!feedData.length) return [];

  // 1. Group by timestamp and average weights
  const grouped: Record<string, { sum: number; count: number }> = {};

  for (const { timestamp, weight } of feedData) {
    const ts = String(timestamp);
    if (!grouped[ts]) {
      grouped[ts] = { sum: 0, count: 0 };
    }
    grouped[ts].sum += weight ?? 0;
    grouped[ts].count += 1;
  }

  const averagedData = Object.entries(grouped)
    .map(([timestamp, { sum, count }]) => ({
      timestamp: Number(timestamp),
      weight: sum / count,
    }))
    .sort((a, b) => a.timestamp - b.timestamp);

  const firstTimestamp = averagedData[0].timestamp;

  // 2. Compute deltas and flow rate
  return averagedData.map((item, index, arr) => {
    const currentTimestamp = item.timestamp;
    const previousTimestamp = index > 0 ? arr[index - 1].timestamp : currentTimestamp;

    const deltaTime = index > 0 ? (currentTimestamp - previousTimestamp) / 1000 : 0;
    const elapsedTime = (currentTimestamp - firstTimestamp) / 1000;

    const prevWeight = index > 0 ? arr[index - 1].weight : item.weight;
    const deltaWeight = item.weight - prevWeight;

    const flowRate = deltaTime === 0 ? 0 : deltaWeight / deltaTime;

    return {
      timestamp: currentTimestamp,
      weight: item.weight,
      deltaTime,
      elapsedTime,
      deltaWeight,
      flowRate,
    };
  });
};

const getFlowRateStats = (data: ProcessedFeedData[] = []): FlowStats => {
  const flowRates = data.map(v => v.flowRate);
  const n = flowRates.length;
  const mean = n ? flowRates.reduce((sum, val) => sum + val, 0) / n : 0;
  const max = n ? Math.max(...flowRates) : 0;
  const std = n > 1
    ? Math.sqrt(flowRates.reduce((sum, val) => sum + (val - mean) ** 2, 0) / (n - 1))
    : 0;

  return { mean, max, std };
};

const meanFlowRateLastByTime = (
  data: ProcessedFeedData[],
  seconds = 1,
  acceleration = false,
  lastOnly = false,
): number => {
  if (!Array.isArray(data) || data.length === 0) {
    return 0;
  }

  if (lastOnly) {
    const last = data[data.length - 1];
    const value = acceleration ? last.flowAcc ?? 0 : last.flowRate;
    return isNaN(value) ? 0 : value;
  }

  if (seconds <= 0) {
    return 0;
  }

  let timeSum = 0;
  const selected: ProcessedFeedData[] = [];
  const endIndex = acceleration ? 0 : -1; // to match python calculation

  for (let i = data.length - 1; i > endIndex; i--) {
    const dt = data[i].deltaTime ?? 0;
    timeSum += dt;
    selected.unshift(data[i]); // add to beginning

    if (timeSum >= seconds) break;
  }

  const values = selected
    .map(item => acceleration ? item.flowAcc ?? 0 : item.flowRate)
    .filter(v => !isNaN(v));

  if (values.length === 0) return 0;

  const sum = values.reduce((acc, val) => acc + val, 0);
  return sum / values.length;
};

/**
 * Computes flow acceleration (flowRate diff / deltaTime) and appends it to each data row.
 */
const computeFlowAcceleration = (
  dataWithFlowRate: ProcessedFeedData[],
): ProcessedFeedData[] => {
  return dataWithFlowRate.map((item, index, arr) => {
    if (index === 0) {
      return { ...item, flowAcc: 0 };
    }

    const prev = arr[index - 1];
    const deltaTime = item.deltaTime ?? 0;

    const flowRateDiff = (item.flowRate ?? 0) - (prev.flowRate ?? 0);

    const flowAcc = deltaTime === 0
      ? 0  // avoid division by zero
      : flowRateDiff / deltaTime;

    return {
      ...item,
      flowAcc,
    };
  });
};

/**
 * Calculates mean, max, and standard deviation of `flowAcc` values in the data array.
 */
const computeFlowAccStats = (data: ProcessedFeedData[]): FlowStats => {
  const values = data
    .slice(1) // exclude first item with flowAcc = 0
    .map(item => item.flowAcc ?? 0)
    .filter(v => !isNaN(v));
  const n = values.length;
  if (n === 0) {
    return { mean: 0, max: 0, std: 0 };
  }
  const mean = values.reduce((sum, val) => sum + val, 0) / n;
  const max = Math.max(...values);

  const std = n > 1
    ? Math.sqrt(values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / (n - 1))
    : 0;

  return { mean, max, std };
};

/**
 * Calculate inflow center time, total duration, weight change, and flow skew.
 */
const computeFlowMetrics = (data: ProcessedFeedData[]): FlowMetrics => {
  if (!data.length) return {
    totalDuration: 0,
    totalWeightChange: 0,
    flowSkew: 0,
    lastWeight: 0,
  };

  // Sum of deltaWeight
  const totalWeight = data.reduce((sum, item) => sum + (item.deltaWeight ?? 0), 0);

  // Sum of deltaWeight * elapsedTime
  const weightedTimeSum = data.reduce(
    (sum, item) => sum + ((item.deltaWeight ?? 0) * (item.elapsedTime ?? 0)),
    0,
  );

  const inflowCenterTime = totalWeight > 0 ? weightedTimeSum / totalWeight : 0;
  const firstTimestamp = new Date(data[0].timestamp);
  const lastTimestamp = new Date(data[data.length - 1].timestamp);
  const totalDuration = (lastTimestamp.getTime() - firstTimestamp.getTime()) / 1000; // ms to seconds
  const lastWeight = data[data.length - 1].weight;
  const totalWeightChange = (data[data.length - 1].weight ?? 0) - (data[0].weight ?? 0);
  const flowSkew = totalDuration > 0 ? inflowCenterTime / totalDuration : 0;

  return {
    totalDuration,
    totalWeightChange,
    flowSkew,
    lastWeight,
  };
};

const getActivePercentage = (status: (number | undefined)[]): number => {
  const activeCount = status.filter(v => v === 1).length;
  const percentage = (activeCount / status.length) * 100;
  return +percentage.toFixed(2); // returns as number, not string
};

type ResultStats = {
  activated: number;
  totalActiveTime: number;
  avgActiveTime: number;
  totalNonActiveTime: number;
  avgNonActiveTime: number;
  activityChangeFreq: number;
};

interface Block {
  value: number | null;
  start: number | null;
  end: number;
  duration?: number;
}

const calcActivityTime = (data: FeedDataInput[]): Record<string, ResultStats> => {
  if (data.length === 0) return {};

  const keys = Object.keys(data[0]).filter(k => k.endsWith('PNoStatus'));
  const results: Record<string, ResultStats> = {};

  keys.forEach(key => {
    const blocks: Block[] = [];
    let prevValue: number | null = null;
    let blockStart: number | null = null;

    for (let i = 0; i < data.length; i++) {
      const currValue = data[i][key];
      const currTime = data[i].timestamp;

      if (currValue !== prevValue) {
        if (prevValue !== null && blockStart !== null) {
          blocks.push({
            value: prevValue,
            start: blockStart,
            end: data[i - 1].timestamp,
          });
        }
        blockStart = currTime;
        prevValue = currValue;
      }
    }

    // Push last block
    if (prevValue !== null && blockStart !== null) {
      blocks.push({
        value: prevValue,
        start: blockStart,
        end: data[data.length - 1].timestamp,
      });
    }

    const filteredBlocks = blocks
      .map(b => ({
        ...b,
        duration: (b.end - (b.start ?? b.end)) / 1000, // convert ms to seconds
      }))
      .filter(b => b.duration !== undefined);

    const activeBlocks = filteredBlocks.filter(b => b.value && b.value > 0);
    const nonActiveBlocks = filteredBlocks.filter(b => b.value === 0);

    const avg = (arr: number[]) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);
    const sum = (arr: number[]) => (arr.length ? arr.reduce((a, b) => a + b, 0) : 0);

    results[key] = {
      activated: Math.max(...filteredBlocks.map(b => b.value ?? 0)), // activeBlocks.length,
      totalActiveTime: sum(activeBlocks.map(b => b.duration!)),
      avgActiveTime: avg(activeBlocks.map(b => b.duration!)),
      totalNonActiveTime: sum(nonActiveBlocks.map(b => b.duration!)),
      avgNonActiveTime: avg(nonActiveBlocks.map(b => b.duration!)),
      activityChangeFreq: blocks.length,
    };
  });

  return results;
};

const addPrefixToObjectKeys = <T extends Record<string, any>>(
  obj: T,
  prefix = 'prev',
): Record<string, any> => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      const newKey = prefix + key.charAt(0).toUpperCase() + key.slice(1);
      return [newKey, value];
    }),
  );
};

const pickDefinedProperties = <T, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> => {
  const result = {} as Pick<T, K>;
  for (const key of keys) {
    if (obj[key] !== undefined) {
      result[key] = obj[key];
    }
  }
  return result;
};

const getAverage = (arr = []) => {
  if (!arr?.length) {
    return 0;
  }
  const sum = arr.reduce((acc, val) => acc + val, 0);
  return sum / arr.length;
};

const getPrevMetrics = (prevFeedMetrics: FeedMetrics) => {
  // @ts-ignore
  return addPrefixToObjectKeys(pickDefinedProperties(prevFeedMetrics, prevFeedProperties));
};

function camelToSnakeWithNumbers(str: string): string {
  return str
    // insert underscore between lowercase/digit and uppercase (normal camelCase)
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    // insert underscore between letters and digits
    .replace(/([a-zA-Z])([0-9])/g, '$1_$2')
    .toLowerCase();
}

export function keysToEnhancedSnakeCase<T>(obj: T): any {
  if (Array.isArray(obj)) {
    return obj.map(keysToEnhancedSnakeCase);
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((acc: any, key: string) => {
      const newKey = camelToSnakeWithNumbers(key);
      acc[newKey] = keysToEnhancedSnakeCase((obj as any)[key]);
      return acc;
    }, {});
  }
  return obj;
}
